import { Component, ChangeDetectionStrategy, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { AccountPlan } from '../../../accounting/model/account-plan';
import { AccountPlanService } from '../../../accounting/service/data/account-plan.service';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormCommandHandlerResolver } from '../../../core/service/command/form-command-handler-resolver.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { ActivatedRoute } from '@angular/router';
import { standardCommand } from '../../../core/model/command';
import { PriceListService } from '../../service/pricelist.service';
import { PriceList } from '../../model/pricelist';
import { SelectionItem } from '../../../core/component/select/select.component';
import { CurrencySelection } from '../../../core/pipe/currency.pipe';
import { PriceListItem } from '../../model/priceListItem';
import { CurrencySymbol, Money } from '../../../core/model/money';
import { LocalDate } from '../../../core/model/local-date';
import { MeterMediaTypeSelection } from '../../model/const/enums';
import { CompanyService } from '../../../rent/service/data/company.service';
import { ArticleService } from '../../../rent/service/data/article.service';
import { Article } from '../../../rent/model/article';
import { switchMap } from 'rxjs/operators';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { SidebarService } from '../../../core/service/sidebar.service';
import { FormControlEx } from '../../../core/component/forms/form-control';
import {
  ColdWaterPaymentKindToString,
  HeatingPaymentKindToString,
  HotWaterPaymentKindToString,
  HouseholdElectricityPaymentKindToString,
  ParkingElectricityPaymentKindToString,
} from '../../../rent/model/const/enums';
import { ImdPriceListRentalObject } from '../../model/pricelist';

@Component({
    selector: 'app-imd-pricelist-form',
    templateUrl: './pricelist-form.component.html',
    styleUrls: ['./pricelist-form.component.scss'],
    providers: [DeleteFormCommandHandler, FormCommandHandlerResolver, { provide: DataService, useExisting: PriceListService }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ImdPriceListFormComponent extends FormComponentBase<PriceList> {
  @ViewChild('priceEditorSidebar', { static: false }) priceEditorSidebar: TemplateRef<any>;

  constructor(
    private _fb: FormBuilder,
    private _fc: FormControlExFactory,
    private _companyService: CompanyService,
    private _articleService: ArticleService,
    private _columnDefinitionFactory: ColumnDefinitionFactory,
    private _sidebarService: SidebarService,
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    handlerResolver: FormCommandHandlerResolver,
    private _service: PriceListService,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, _service, messageService, navigation, commandDispatcher, handlerResolver);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
    this.priceColumnDefinition = [
      this._columnDefinitionFactory.createDate('CONSUMPTION.PRICELIST.VALID_FROM', 'fromDate'),
      this._columnDefinitionFactory.createMoney('CONSUMPTION.PRICELIST.PRICE_PER_UNIT', 'amountPerUnit', { totalSum: false }),
    ];
    this.rentalObjectColumnDefinition = [
      this._columnDefinitionFactory.create('RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId', ColumnVisibility.default),
      this._columnDefinitionFactory.create('RENTAL_OBJECT_LIST.STREET_COLUMN', 'street', ColumnVisibility.optional),
      this._columnDefinitionFactory.create('RENTAL_OBJECT_LIST.RENTAL_OBJECT_TYPE_COLUMN', 'rentalObjectType', ColumnVisibility.default),
      this._columnDefinitionFactory.create('CONSUMPTION.PRICELIST.ELECTRICITY_METER', 'electricityMeterName'),
      this._columnDefinitionFactory.create('CONSUMPTION.PRICELIST.CHARGING_ELECTRICITY_METER', 'chargingElectricityMeterName'),
      this._columnDefinitionFactory.create('CONSUMPTION.PRICELIST.HOT_WATER_METER', 'hotWaterMeterName'),
      this._columnDefinitionFactory.create('CONSUMPTION.PRICELIST.COLD_WATER_METER', 'coldWaterMeterName'),
      this._columnDefinitionFactory.create('CONSUMPTION.PRICELIST.HEATING_METER', 'heatingMeterName'),
      this._columnDefinitionFactory.createTranslation('LEASE_AGREEMENT.HOUSEHOLD_ELECTRICITY_PAYMENT', 'householdElectricityPaymentCaption'),
      this._columnDefinitionFactory.createTranslation(
        'CONSUMPTION.PRICELIST.CHARGING_ELECTRICITY_PAYMENT',
        'parkingChargingElectricityPaymentCaption'
      ),
      this._columnDefinitionFactory.createTranslation('LEASE_AGREEMENT.HEATING_PAYMENT', 'heatingPaymentCaption'),
      this._columnDefinitionFactory.createTranslation('LEASE_AGREEMENT.COLD_WATER_PAYMENT', 'coldWaterPaymentCaption'),
      this._columnDefinitionFactory.createTranslation('LEASE_AGREEMENT.HOT_WATER_PAYMENT', 'hotWaterPaymentCaption'),
    ];
  }

  protected createModel(): PriceList {
    return new PriceList();
  }

  private _articles: Article[];
  articleSelection: SelectionItem[];
  meterTypeSelection = MeterMediaTypeSelection;
  currencySelection: SelectionItem[];
  priceColumnDefinition: ColumnDefinition<PriceListItem>[];
  rentalObjectColumnDefinition: ColumnDefinition<ImdPriceListRentalObject>[];
  priceItems: PriceListItem[] = [];
  propertySelection: SelectionItem[] = [];
  propertyControl: FormControlEx;
  rentalObjects: ImdPriceListRentalObject[] = [];
  priceEditorForm: FormGroup;
  editedPriceIndex: number = null;
  private _isPriceEditorSidebarOpen = false;

  private updateArticleSelection() {
    var currencyCode = this.form.controls['currencyCode'].value;
    if (this._articles) {
      this.articleSelection = this._articles.filter(a => a.currencyCode === currencyCode).map(a => a.toSelectionItem());
    }
  }

  init(data: PriceList) {
    super.init(data);
    this.priceItems = [];

    this.AddSubscription(
      this._companyService
        .getUsedCurrencies()
        .pipe(
          switchMap(res => {
            this.currencySelection = res.map(c => new SelectionItem(CurrencySymbol(c), c));
            if (this.isNew) {
              this.form.controls['currencyCode'].setValue(this.currencySelection[0].value);
            } else {
              let currencyCode = this.form.controls['currencyCode'].value;
              if (!this.currencySelection.find(c => c.value === currencyCode)) {
                this.currencySelection.push(new SelectionItem(CurrencySymbol(currencyCode), currencyCode));
              }
            }
            return this._articleService.getList();
          })
        )
        .subscribe(articles => {
          this._articles = articles;
          this.updateArticleSelection();
        })
    );

    if (this.isNew) {
      this.AddSubscription(
        this.form.controls['currencyCode'].valueChanges.subscribe(val => {
          for (let priceForm of this.prices.controls.map(c => c as FormGroup)) {
            priceForm.controls['amountPerUnit'].setValue(new Money(val, priceForm.controls['amountPerUnit'].value.amount));
          }
          for (let price of this.priceItems) {
            price.amountPerUnit = new Money(val, price.amountPerUnit.amount);
          }
          this.updateArticleSelection();
        })
      );
    } else {
      for (let price of this.dataContext.prices) {
        this.prices.controls.push(this.createPriceForm(price));
        this.priceItems.push(price);
      }

      this.propertyControl = this._fc.create(
        'CONSUMPTION.PRICELIST.PROPERTY',
        null,
        null,
        null,
        'CONSUMPTION.PRICELIST.PROPERTY_HELP'
      );
      this.AddSubscription(this.propertyControl.valueChanges.subscribe(propertyId => this.loadRentalObjects(propertyId)));
      this.AddSubscription(
        this._service.getProperties(this.dataContext.id).subscribe(properties => {
          this.propertySelection = [
            new SelectionItem('CONSUMPTION.PRICELIST.NO_PROPERTY_SELECTED', null),
            ...properties.map(property => new SelectionItem(property.name, property.id)),
          ];
        })
      );
    }
  }

  prices: FormArray;

  addPrice() {
    this.editedPriceIndex = null;
    this.priceEditorForm = this.createPriceEditorForm(this.createNewPrice());
    this.openPriceEditorSidebar();
  }

  private createNewPrice(): PriceListItem {
    let price = new PriceListItem();
    price.amountPerUnit = new Money(this.form.controls['currencyCode'].value, 0);
    let fromDate = LocalDate.today();
    fromDate.addMonths(1);
    fromDate = LocalDate.fromDate(fromDate.firstDayInMonth());
    price.fromDate = fromDate;
    return price;
  }

  removePrice(index: number) {
    this.prices.removeAt(index);
    this.priceItems.splice(index, 1);
    this.form.markAsDirty();
  }

  onPriceRowClicked(event: { context: PriceListItem; event: Event }) {
    const index = this.priceItems.indexOf(event.context);
    if (index === -1) {
      return;
    }

    this.editedPriceIndex = index;
    this.priceEditorForm = this.createPriceEditorForm(event.context);
    this.openPriceEditorSidebar();
  }

  savePrice(): void {
    this.priceEditorForm.markAllAsTouched();
    if (this.priceEditorForm.invalid) {
      return;
    }

    const price = this.toPriceListItem(this.priceEditorForm.getRawValue());
    if (this.editedPriceIndex === null) {
      this.prices.push(this.createPriceForm(price));
      this.priceItems.push(price);
    } else {
      this.prices.at(this.editedPriceIndex).patchValue(price);
      Object.assign(this.priceItems[this.editedPriceIndex], price);
    }

    this.form.markAsDirty();
    this.closePriceEditorSidebar();
  }

  deletePrice(): void {
    if (this.editedPriceIndex === null) {
      return;
    }

    this.removePrice(this.editedPriceIndex);
    this.closePriceEditorSidebar();
  }

  cancelPriceEditor(): void {
    this.closePriceEditorSidebar();
  }

  private openPriceEditorSidebar(): void {
    this._isPriceEditorSidebarOpen = true;
    this._sidebarService.setSidebarContent({
      template: this.priceEditorSidebar,
      onClose: () => this.resetPriceEditor(),
    });
    this._sidebarService.toggleSidebar(true);
  }

  private closePriceEditorSidebar(): void {
    this.resetPriceEditor();
    this._sidebarService.toggleSidebar(false);
  }

  private resetPriceEditor(): void {
    this.priceEditorForm = null;
    this.editedPriceIndex = null;
    this._isPriceEditorSidebarOpen = false;
  }

  private toPriceListItem(value: PriceListItem): PriceListItem {
    return Object.assign(new PriceListItem(), value);
  }

  private createPriceForm(price: PriceListItem) {
    var form = this._fb.group({
      id: this._fc.createHidden(price ? price.id : null),
      amountPerUnit: this._fc.createMandatory(
        'CONSUMPTION.PRICELIST.PRICE_PER_UNIT',
        price ? price.amountPerUnit : new Money(this.form.controls['currencyCode'].value, 0)
      ),
      fromDate: this._fc.createMandatory('CONSUMPTION.PRICELIST.VALID_FROM', price ? price.fromDate : null),
    });
    this.AddSubscription(
      form.valueChanges.subscribe(x => {
        this.prices.updateValueAndValidity();
        this.form.markAsDirty();
      })
    );
    return form;
  }

  private createPriceEditorForm(price: PriceListItem): FormGroup {
    return this._fb.group({
      id: this._fc.createHidden(price?.id ?? null),
      amountPerUnit: this._fc.createMandatory(
        'CONSUMPTION.PRICELIST.PRICE_PER_UNIT',
        price?.amountPerUnit ?? new Money(this.form.controls['currencyCode'].value, 0)
      ),
      fromDate: this._fc.createMandatory('CONSUMPTION.PRICELIST.VALID_FROM', price?.fromDate ?? null),
    });
  }

  private loadRentalObjects(propertyId: string): void {
    this.rentalObjects = [];
    if (!propertyId) {
      return;
    }

    this.AddSubscription(
      this._service.getRentalObjects(this.dataContext.id, propertyId).subscribe(rentalObjects => {
        if (this.propertyControl.value !== propertyId) {
          return;
        }

        this.rentalObjects = rentalObjects.map(rentalObject => ({
            ...rentalObject,
            householdElectricityPaymentCaption: this.paymentCaption(
              rentalObject.householdElectricityPayment,
              HouseholdElectricityPaymentKindToString
            ),
            parkingChargingElectricityPaymentCaption: this.paymentCaption(
              rentalObject.parkingChargingElectricityPayment,
              ParkingElectricityPaymentKindToString
            ),
            heatingPaymentCaption: this.paymentCaption(rentalObject.heatingPayment, HeatingPaymentKindToString),
            coldWaterPaymentCaption: this.paymentCaption(rentalObject.coldWaterPayment, ColdWaterPaymentKindToString),
            hotWaterPaymentCaption: this.paymentCaption(rentalObject.hotWaterPayment, HotWaterPaymentKindToString),
          }));
      })
    );
  }

  private paymentCaption(value: number | null | undefined, toTranslationKey: (value: any) => string): string {
    return value === null || value === undefined ? 'CONST.NONE' : toTranslationKey(value);
  }

  onSubmit() {
    super.onSubmit();
  }

  override innerFormDirty(): boolean {
    return this.priceEditorForm?.dirty ?? false;
  }

  override ngOnDestroy(): void {
    if (this._isPriceEditorSidebarOpen) {
      this._sidebarService.toggleSidebar(false);
    }
    super.ngOnDestroy();
  }

  prepareSave(): PriceList {
    let result = super.prepareSave();
    result.prices = this.prices.controls.map(c => c.value);
    return result;
  }

  protected getFormGroup(): FormGroup {
    this.prices = this._fb.array([]);
    return this._fb.group({
      id: this._fc.createHidden(this.dataContext.id),
      name: this._fc.createMandatory('CONSUMPTION.PRICELIST.NAME', this.dataContext.name),
      currencyCode: this._fc.createWithOptions('COMMON.CURRENCY', this.dataContext.currencyCode, {
        isReadonly: !this.isNew,
        required: true,
      }),
      meterType: this._fc.createWithOptions('CONSUMPTION.MEDIA_TYPE.TYPE', this.dataContext.meterType, {
        isReadonly: !this.isNew,
        required: true,
      }),
      articleId: this._fc.createMandatory('LEASE_AGREEMENT_ROW.ARTICLE', this.dataContext.articleId),
      prices: this.prices,
    });
  }
}
